import {useCallback, useState} from 'react'
import {useIsOverviewEnabled} from '../hooks/use-feature-state'
import {isLoggedIn} from '@github-ui/client-env'
import type {HeaderPageData} from '../page-data/payloads/header'
import {ActionList, ActionMenu, Button, Label, Link, Popover} from '@primer/react'
import {ArrowSwitchIcon, ArrowUpRightIcon, CommentIcon, RocketIcon} from '@primer/octicons-react'
import {toggleGenericFeaturePath} from '@github-ui/paths'
import {verifiedFetch} from '@github-ui/verified-fetch'
import type {NavigationUrls} from '../types/navigation-urls-types'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useDismissNotice} from '../mutations/use-dismiss-notice'
import {useIsNoticeDismissed} from '../stores/layout-selectors'
import styles from './FilesChangedFeedback.module.css'
import {clsx} from 'clsx'
import {Notices} from '../page-data/payloads/notices'

export function FilesChangedFeedback({
  repository,
  urls,
  renderLocation,
}: {
  repository: HeaderPageData['repository']
  urls: NavigationUrls
  renderLocation: 'mobile' | 'desktop'
}) {
  const onClick = async () => {
    const formData = new FormData()
    formData.append('feature_name', 'prx_files')

    await verifiedFetch(toggleGenericFeaturePath({repo: repository}), {
      body: formData,
      method: 'POST',
    })
  }

  const shouldShowLoggedInOptions = isLoggedIn()

  const pathname = ssrSafeLocation.pathname ?? ''
  const search = ssrSafeLocation.search ?? ''
  let switchBackUrl: string
  if (pathname) {
    switchBackUrl = `${pathname}${search ? `${search}&` : '?'}new_files_changed=false`
  } else {
    // Fallback to NavigationUrls for SSR or when location is unavailable
    switchBackUrl = `${urls.files}?new_files_changed=false`
  }

  const showUpdatedHeader = useIsOverviewEnabled()
  return (
    <div className={clsx('position-relative pl-1', !showUpdatedHeader && 'flex-order-2')}>
      <ActionMenu>
        <ActionMenu.Button variant="invisible" size="small">
          <Label variant="success" className={styles.PreviewLabel}>
            Preview
          </Label>
        </ActionMenu.Button>
        <ActionMenu.Overlay>
          <ActionList>
            <ActionList.LinkItem
              href="https://gh.io/new-files-changed-changelog"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ActionList.LeadingVisual>
                <RocketIcon />
              </ActionList.LeadingVisual>
              What&apos;s new
              <ActionList.TrailingVisual>
                <ArrowUpRightIcon />
              </ActionList.TrailingVisual>
            </ActionList.LinkItem>
            <ActionList.LinkItem
              href="https://gh.io/new-files-changed-feedback"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ActionList.LeadingVisual>
                <CommentIcon />
              </ActionList.LeadingVisual>
              Give feedback
              <ActionList.TrailingVisual>
                <ArrowUpRightIcon />
              </ActionList.TrailingVisual>
            </ActionList.LinkItem>
            {shouldShowLoggedInOptions && (
              <>
                <ActionList.Divider />
                <ActionList.LinkItem
                  href={switchBackUrl}
                  onClick={async e => {
                    e.preventDefault()
                    await onClick()
                    window.location.href = switchBackUrl
                  }}
                >
                  <ActionList.LeadingVisual>
                    <ArrowSwitchIcon />
                  </ActionList.LeadingVisual>
                  Switch to the classic experience
                </ActionList.LinkItem>
              </>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <FilesChangedOptOutPopover renderLocation={renderLocation} />
    </div>
  )
}

function FilesChangedOptOutPopover({renderLocation}: {renderLocation: 'mobile' | 'desktop'}) {
  const isNoticeDismissed = useIsNoticeDismissed(Notices.FilesChangedOptOut)
  const {dismissNotice} = useDismissNotice(Notices.FilesChangedOptOut)
  const caretPosition = renderLocation === 'mobile' ? 'top-left' : 'top-right'

  const [popoverOpen, setPopoverOpen] = useState(true)

  const dismissPopover = useCallback(() => {
    setPopoverOpen(false)
    dismissNotice()
  }, [dismissNotice])

  if (isNoticeDismissed) return null

  return (
    <Popover
      open={popoverOpen}
      caret={caretPosition}
      className={clsx(styles.PopoverContainer, {
        [styles.PopoverContainerDesktop]: renderLocation === 'desktop',
        [styles.PopoverContainerMobile]: renderLocation === 'mobile',
      })}
    >
      <Popover.Content className={clsx('d-flex flex-column gap-2', styles.PopoverContent)}>
        <h2 className={clsx('lh-default', styles.Heading)}>Welcome to the upgraded Files Changed experience</h2>
        <p className="color-fg-muted f5 text-normal lh-default">
          It&apos;s faster, more accessible, and includes new features like commenting anywhere in changed files and
          file tree indicators.
        </p>
        <div className="d-flex gap-2 flex-row w-full flex-wrap">
          <Button
            as={Link}
            trailingVisual={<ArrowUpRightIcon />}
            target="_blank"
            rel="noopener noreferrer"
            href="https://gh.io/new-files-changed-changelog"
            onClick={dismissPopover}
          >
            See what&apos;s new
          </Button>
          <Button onClick={dismissPopover} variant="invisible">
            Dismiss
          </Button>
        </div>
      </Popover.Content>
    </Popover>
  )
}
